import { baseApi } from './baseApi';
import { addNotification } from '../store';
import { isAbortError } from '../helpers/apiErrors';
import { ServiceTypes } from '../constants';
import { captureError } from '@clatter/platform';

export const presentationsThemesService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPresentationThemes: builder.query<PresentationTheme[], void>({
      query: () => '/presentation-themes',
      transformResponse: (result: INgApiResponse<PresentationTheme[]>) => result.data,
      providesTags: () => [{ type: ServiceTypes.PRESENTATION_THEMES }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          const message = 'Couldn\'t load the presentation themes. Please try again.';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          captureError(error, { message, component: 'presentationThemes.service' });
        }
      },
    }),
    getPresentationThemeById: builder.query<PresentationTheme, { themeId: number }>({
      query: ({ themeId }) => `/presentation-themes/${themeId}`,
      transformResponse: (result: INgApiResponse<PresentationTheme>) => result.data,
      providesTags: () => [{ type: ServiceTypes.PRESENTATION_THEMES }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          const message = 'Couldn\'t load the presentation themes. Please try again.';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          captureError(error, { message, component: 'presentationThemes.service' });
        }
      },
    }),
    getPresentationThemeLayouts: builder.query<ThemeLayoutGroup[], { themeId: number }>({
      query: ({ themeId }) => `/presentation-themes/${themeId}/layouts`,
      transformResponse: (result: INgApiResponse<ThemeLayoutGroup[]>) => result.data,
      providesTags: () => [{ type: ServiceTypes.PRESENTATION_THEMES }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          const message = 'Couldn\'t load the theme layouts. Please try again.';
          dispatch(addNotification({ type: 'error', message }));
          captureError(error, { message, component: 'presentationThemes.service' });
        }
      },
    }),
    updatePresentationTheme: builder.mutation<
      PresentationTheme,
      {
        themeId: number;
        data: { is_default?: boolean } & {
          layouts?: Array<Pick<PresentationThemeLayout, 'name' | 'index' | 'values_schema'>>;
        };
      }
    >({
      query: ({ themeId, data }) => ({
        url: `/presentation-themes/${themeId}`,
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (result: INgApiResponse<PresentationTheme>) => result.data,
      invalidatesTags: () => [{ type: ServiceTypes.PRESENTATION_THEMES }],
      async onQueryStarted({ data }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (data.is_default) {
            dispatch(addNotification({ type: 'success', message: 'Default theme updated successfully.' }));
          }
        } catch (error: any) {
          if (isAbortError(error)) return;
          const message = 'Couldn\'t update the presentation theme. Please try again.';
          dispatch(addNotification({ type: 'error', message }));
          captureError(error, { message, component: 'presentationThemes.service' });
        }
      },
    }),
    updatePresentationThemeLayout: builder.mutation<
      { id: number; hidden: boolean },
      { themeId: number; layoutId: number; hidden: boolean }
    >({
      query: ({ themeId, layoutId, hidden }) => ({
        url: `/presentation-themes/${themeId}/layouts/${layoutId}`,
        method: 'PATCH',
        body: { hidden },
      }),
      transformResponse: (result: INgApiResponse<{ id: number; hidden: boolean }>) => result.data,
      invalidatesTags: () => [{ type: ServiceTypes.PRESENTATION_THEMES }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          const message = 'Couldn\'t update the layout visibility. Please try again.';
          dispatch(addNotification({ type: 'error', message }));
          captureError(error, { message, component: 'presentationThemes.service' });
        }
      },
    }),
  }),
});

export const {
  useGetPresentationThemesQuery,
  useGetPresentationThemeByIdQuery,
  useGetPresentationThemeLayoutsQuery,
  useUpdatePresentationThemeMutation,
  useUpdatePresentationThemeLayoutMutation,
} = presentationsThemesService;
